import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';

export type AuthMode = 'cognito' | 'disabled';

export interface CognitoAuthConfig {
  mode: 'cognito';
  userPoolId: string;
  userPoolClientId: string;
  domain: string;
  scopes: string[];
  redirectSignIn: string;
  redirectSignOut: string;
}

export interface DisabledAuthConfig {
  mode: 'disabled';
}

export type AuthConfig = CognitoAuthConfig | DisabledAuthConfig;

export interface AuthConfigError {
  reason: 'invalid-mode' | 'missing-env';
  missingKeys: string[];
}

export type AuthConfigResult =
  { ok: true; config: AuthConfig } | { ok: false; error: AuthConfigError };

/** APIの /v1/* が要求するcustom scope。CDKのresource serverと揃える。 */
export const DEFAULT_API_SCOPE = 'poc4-api/access';

// cognito modeで必須の環境変数。1つでも欠けたらログインを試さず設定エラーにする。
// scopeは既定値を持つので含めない。
const REQUIRED_COGNITO_KEYS = [
  'VITE_COGNITO_USER_POOL_ID',
  'VITE_COGNITO_APP_CLIENT_ID',
  'VITE_COGNITO_DOMAIN',
  'VITE_COGNITO_REDIRECT_SIGN_IN',
  'VITE_COGNITO_REDIRECT_SIGN_OUT',
] as const;

/**
 * 環境変数から認証設定を作る。envを引数で受けるのは、テストでimport.meta.envを
 * 差し替える細工をせずに済ませるため。
 *
 * 既定はcognito。disabledはlocal/E2Eで明示する（既定をdisabledにすると
 * 設定漏れが「認証なしで動く」に化けるため）。
 */
export function loadAuthConfig(env: ImportMetaEnv): AuthConfigResult {
  const mode = env.VITE_AUTH_MODE ?? 'cognito';
  if (mode === 'disabled') {
    return { ok: true, config: { mode: 'disabled' } };
  }
  if (mode !== 'cognito') {
    return { ok: false, error: { reason: 'invalid-mode', missingKeys: ['VITE_AUTH_MODE'] } };
  }

  const missingKeys = REQUIRED_COGNITO_KEYS.filter((key) => !env[key]);
  if (missingKeys.length > 0) {
    return { ok: false, error: { reason: 'missing-env', missingKeys: [...missingKeys] } };
  }

  return {
    ok: true,
    config: {
      mode: 'cognito',
      userPoolId: env.VITE_COGNITO_USER_POOL_ID!,
      userPoolClientId: env.VITE_COGNITO_APP_CLIENT_ID!,
      domain: env.VITE_COGNITO_DOMAIN!,
      // Managed Loginではopenid / emailも要求する。API側が見るのはcustom scopeだけ。
      scopes: ['openid', 'email', env.VITE_COGNITO_SCOPE || DEFAULT_API_SCOPE],
      redirectSignIn: env.VITE_COGNITO_REDIRECT_SIGN_IN!,
      redirectSignOut: env.VITE_COGNITO_REDIRECT_SIGN_OUT!,
    },
  };
}

/**
 * Amplifyを設定する。cognito modeで1回だけ呼ぶ。
 *
 * tokenの保存先をsessionStorageにするのはここだけ。既定のlocalStorageは
 * タブを閉じても残るため、XSSでtokenを抜かれる時間窓が長くなる。
 */
export function configureAmplify(config: CognitoAuthConfig): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.domain,
            scopes: config.scopes,
            redirectSignIn: [config.redirectSignIn],
            redirectSignOut: [config.redirectSignOut],
            // Authorization Code Grant + PKCE。implicit flowは使わない。
            responseType: 'code',
          },
        },
      },
    },
  });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
}
